import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Save, X, Download, Users, Lock, Unlock, FileText, UserPlus, Undo2, CreditCard, Megaphone, Layers } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQ = { withCredentials: true };

const InventoryManagement = () => {
  const { flightId } = useParams();
  const navigate = useNavigate();
  const [flight, setFlight] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [editForm, setEditForm] = useState({ pnr: '', total_seats: '', status: '' });
  const [agentBlocks, setAgentBlocks] = useState([]);
  const [blockModalDate, setBlockModalDate] = useState(null);
  const [agents, setAgents] = useState([]);
  const [blockForm, setBlockForm] = useState({
    agent_id: '',
    seats: 1,
    fare_per_seat: '',
    debit_mode: 'wallet',
    remarks: ''
  });
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockRequests, setBlockRequests] = useState([]);
  const [agentLabelById, setAgentLabelById] = useState({});
  const [releaseModal, setReleaseModal] = useState(null);
  const [releaseForm, setReleaseForm] = useState({
    seats_to_release: '',
    penalty_style: 'none',
    penalty_percent: 15,
    penalty_fixed_per_seat: 0,
    remarks: ''
  });
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [channelModal, setChannelModal] = useState(null);
  const [channelForm, setChannelForm] = useState({
    publish_special_deals: false,
    publish_special_deals_max: 0,
    publish_agent_blocks: false,
    publish_agent_blocks_max: 0,
  });
  const [channelSaving, setChannelSaving] = useState(false);
  const [bulkChannelOpen, setBulkChannelOpen] = useState(false);
  const [bulkChannelForm, setBulkChannelForm] = useState({
    publish_special_deals: true,
    publish_special_deals_max: '',
    publish_agent_blocks: true,
    publish_agent_blocks_max: '',
  });

  const todayStr = () => new Date().toISOString().slice(0, 10);

  useEffect(() => {
    fetchFlightDetails();
  }, [flightId]);

  const fetchFlightDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/offline-flights/${flightId}`, REQ);
      if (response.data.success) {
        setFlight(response.data.data);
        setInventory(response.data.data.inventory);
        try {
          const br = await axios.get(`${API}/admin/offline-flights/${flightId}/agent-seat-blocks`, REQ);
          if (br.data.success) setAgentBlocks(br.data.data || []);
        } catch (e) {
          console.error(e);
        }
        try {
          const pr = await axios.get(`${API}/admin/offline-flights/${flightId}/agent-seat-block-requests`, REQ);
          if (pr.data.success) setBlockRequests(pr.data.data || []);
        } catch (e) {
          console.error(e);
        }
        try {
          const ar = await axios.get(`${API}/admin/agents`, { ...REQ, params: { page: 1, limit: 500 } });
          const map = {};
          (ar.data.agents || []).forEach((a) => {
            map[a.id] = a.agency_name || a.full_name || a.email || a.id;
          });
          setAgentLabelById(map);
        } catch (e) {
          console.error(e);
        }
      }
    } catch (error) {
      console.error('Error fetching flight:', error);
      alert('Failed to fetch flight details');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (inv) => {
    setEditingDate(inv.date);
    setEditForm({
      pnr: inv.pnr,
      total_seats: inv.total_seats,
      status: inv.status
    });
  };

  const cancelEditing = () => {
    setEditingDate(null);
    setEditForm({ pnr: '', total_seats: '', status: '' });
  };

  const saveInventory = async (date) => {
    try {
      const response = await axios.put(
        `${API}/admin/offline-flights/${flightId}/inventory/${date}`,
        editForm,
        REQ
      );

      if (response.data.success) {
        alert('Inventory updated successfully');
        fetchFlightDetails();
        cancelEditing();
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert(error.response?.data?.detail || 'Failed to update inventory');
    }
  };

  const blockInventory = async (date) => {
    if (!window.confirm('Block this inventory date?')) return;

    try {
      const response = await axios.post(`${API}/admin/offline-flights/${flightId}/block/${date}`, {}, REQ);
      if (response.data.success) {
        alert('Inventory blocked');
        fetchFlightDetails();
      }
    } catch (error) {
      console.error('Error blocking inventory:', error);
      alert('Failed to block inventory');
    }
  };

  const unblockInventory = async (date) => {
    if (!window.confirm('Unblock this inventory date?')) return;

    try {
      const response = await axios.post(`${API}/admin/offline-flights/${flightId}/unblock/${date}`, {}, REQ);
      if (response.data.success) {
        alert('Inventory unblocked');
        fetchFlightDetails();
      }
    } catch (error) {
      console.error('Error unblocking inventory:', error);
      alert('Failed to unblock inventory');
    }
  };

  const downloadManifestCSV = async (date) => {
    try {
      const response = await axios.get(
        `${API}/admin/offline-flights/${flightId}/manifest/${date}/csv`,
        { ...REQ, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manifest_${flightId}_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading manifest:', error);
      alert('Failed to download manifest');
    }
  };

  const openAgentBlockModal = async (date) => {
    setBlockModalDate(date);
    setBlockForm({ agent_id: '', seats: 1, fare_per_seat: '', debit_mode: 'wallet', remarks: '' });
    try {
      const r = await axios.get(`${API}/admin/agents`, { ...REQ, params: { page: 1, limit: 200 } });
      setAgents(r.data.agents || []);
    } catch (e) {
      console.error(e);
      alert('Could not load agents');
    }
  };

  const approveBlockRequest = async (reqId) => {
    if (!window.confirm('Approve this request? Seats will be reserved and the agent wallet debited (or credit/ledger per your choice).')) return;
    try {
      const r = await axios.post(
        `${API}/admin/agent-seat-block-requests/${reqId}/approve`,
        { debit_mode: 'wallet', allow_insufficient_wallet: false },
        REQ
      );
      if (r.data.success) {
        alert('Request approved; seat block created.');
        fetchFlightDetails();
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Approve failed');
    }
  };

  const rejectBlockRequest = async (reqId) => {
    const reason = window.prompt('Optional reason for rejection') || '';
    try {
      const r = await axios.post(`${API}/admin/agent-seat-block-requests/${reqId}/reject`, { reason }, REQ);
      if (r.data.success) {
        alert('Request rejected');
        fetchFlightDetails();
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Reject failed');
    }
  };

  const blockCanRelease = (b) => b && !['TICKETED', 'RELEASED', 'CANCELLED'].includes(b.status);

  const computeReleasePreview = (block, form) => {
    const orig = Number(block.seats_blocked) || 0;
    let n = Number(form.seats_to_release);
    if (!Number.isFinite(n) || n < 1) n = orig;
    n = Math.min(orig, Math.max(1, Math.floor(n)));
    const amt = Number(block.total_amount ?? block.amount_debited ?? 0);
    const gross = orig > 0 ? Math.round(amt * (n / orig) * 100) / 100 : 0;
    let penalty = 0;
    if (form.penalty_style === 'percent') {
      const p = Number(form.penalty_percent) || 0;
      penalty = Math.round(gross * (p / 100) * 100) / 100;
    } else if (form.penalty_style === 'fixed_per_seat') {
      const f = Number(form.penalty_fixed_per_seat) || 0;
      penalty = Math.round(f * n * 100) / 100;
    }
    penalty = Math.min(penalty, gross);
    const net = Math.round((gross - penalty) * 100) / 100;
    return { orig, n, gross, penalty, net };
  };

  const openReleaseModal = (b) => {
    setReleaseModal(b);
    setReleaseForm({
      seats_to_release: String(b.seats_blocked ?? 1),
      penalty_style: 'none',
      penalty_percent: 15,
      penalty_fixed_per_seat: 0,
      remarks: ''
    });
  };

  const submitRelease = async () => {
    if (!releaseModal) return;
    const b = releaseModal;
    const orig = Number(b.seats_blocked) || 0;
    let seatsInput = Number(releaseForm.seats_to_release);
    if (!Number.isFinite(seatsInput) || seatsInput < 1) seatsInput = orig;
    seatsInput = Math.min(orig, Math.max(1, Math.floor(seatsInput)));
    if (releaseForm.penalty_style === 'percent') {
      const p = Number(releaseForm.penalty_percent);
      if (!Number.isFinite(p) || p < 1 || p > 100) {
        alert('Penalty % must be between 1 and 100 when using percentage penalty.');
        return;
      }
    }
    if (releaseForm.penalty_style === 'fixed_per_seat') {
      const f = Number(releaseForm.penalty_fixed_per_seat);
      if (!Number.isFinite(f) || f <= 0) {
        alert('Fixed penalty per seat must be greater than 0.');
        return;
      }
    }
    const pv = computeReleasePreview(b, { ...releaseForm, seats_to_release: String(seatsInput) });
    const debit = (b.debit_mode || 'wallet').toLowerCase();
    const refundHint =
      debit === 'wallet'
        ? 'Wallet blocks: gross refund is credited, then any penalty is debited (two ledger lines when penalty applies).'
        : debit === 'credit_limit'
          ? 'Credit blocks: net amount restores credit headroom (one credit transaction with breakdown in the note).'
          : 'Ledger blocks: inventory only; no automatic money movement.';
    const msg = `Release ${seatsInput} of ${orig} seat(s)?\n\nEstimated gross ₹${pv.gross}, penalty ₹${pv.penalty}, net ₹${pv.net}.\n\n${refundHint}`;
    if (!window.confirm(msg)) return;
    setReleaseBusy(true);
    try {
      const payload = {
        seats_to_release: seatsInput === orig ? undefined : seatsInput,
        penalty_style: releaseForm.penalty_style,
        penalty_percent: Number(releaseForm.penalty_percent) || 0,
        penalty_fixed_per_seat: Number(releaseForm.penalty_fixed_per_seat) || 0,
        remarks: releaseForm.remarks.trim() || undefined
      };
      const r = await axios.post(`${API}/admin/agent-blocks/${b.id}/release-seats`, payload, REQ);
      if (r.data.success) {
        const {
          gross_refund: gr,
          net_refund: nr,
          penalty_total: pt,
          seats_released: sr,
          remaining_seats: rem
        } = r.data;
        let al = `Released ${sr ?? seatsInput} seat(s).`;
        if (rem != null) al += ` Remaining on block: ${rem}.`;
        al += ' Inventory updated.';
        if (gr != null && nr != null) al += ` Refund gross ₹${gr}, penalty ₹${pt ?? 0}, net ₹${nr}.`;
        alert(al);
        setReleaseModal(null);
        fetchFlightDetails();
      }
    } catch (e) {
      console.error(e);
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x) => x.msg || x).join(', ') : 'Release failed');
    } finally {
      setReleaseBusy(false);
    }
  };

  const submitAgentBlock = async () => {
    if (!blockModalDate || !blockForm.agent_id || !blockForm.fare_per_seat) {
      alert('Agent and fare per seat are required');
      return;
    }
    setBlockSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/offline-flights/${flightId}/agent-seat-blocks`,
        {
          agent_id: blockForm.agent_id,
          departure_date: blockModalDate,
          seats: Number(blockForm.seats) || 1,
          fare_per_seat: parseFloat(blockForm.fare_per_seat),
          debit_mode: blockForm.debit_mode,
          remarks: blockForm.remarks || undefined
        },
        REQ
      );
      if (r.data.success) {
        alert('Agent seat block created');
        setBlockModalDate(null);
        fetchFlightDetails();
        const br = await axios.get(`${API}/admin/offline-flights/${flightId}/agent-seat-blocks`, REQ);
        if (br.data.success) setAgentBlocks(br.data.data || []);
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to create block');
    } finally {
      setBlockSaving(false);
    }
  };

  const viewManifest = (date) => {
    navigate(`/admin/offline-inventory/${flightId}/manifest/${date}`);
  };

  const toggleDateSelection = (date) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const openChannelModal = (inv) => {
    setChannelModal(inv);
    setChannelForm({
      publish_special_deals: !!inv.publish_special_deals,
      publish_special_deals_max: inv.publish_special_deals_max ?? inv.available_seats ?? 0,
      publish_agent_blocks: !!inv.publish_agent_blocks,
      publish_agent_blocks_max: inv.publish_agent_blocks_max ?? inv.available_seats ?? 0,
    });
  };

  const saveChannelPublish = async (date, payload) => {
    setChannelSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/offline-flights/${flightId}/inventory/${date}/channel-publish`,
        payload,
        REQ
      );
      if (r.data.success) {
        alert('Channel publish settings saved');
        setChannelModal(null);
        setBulkChannelOpen(false);
        fetchFlightDetails();
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to save channel settings');
    } finally {
      setChannelSaving(false);
    }
  };

  const submitChannelModal = () => {
    if (!channelModal) return;
    const payload = {
      publish_special_deals: channelForm.publish_special_deals,
      publish_agent_blocks: channelForm.publish_agent_blocks,
    };
    if (channelForm.publish_special_deals) {
      payload.publish_special_deals_max = Math.max(1, Number(channelForm.publish_special_deals_max) || 0);
    }
    if (channelForm.publish_agent_blocks) {
      payload.publish_agent_blocks_max = Math.max(1, Number(channelForm.publish_agent_blocks_max) || 0);
    }
    saveChannelPublish(channelModal.date, payload);
  };

  const submitBulkChannel = async () => {
    if (selectedDates.size === 0) {
      alert('Select at least one date');
      return;
    }
    const payload = {
      dates: Array.from(selectedDates),
      publish_special_deals: bulkChannelForm.publish_special_deals,
      publish_agent_blocks: bulkChannelForm.publish_agent_blocks,
    };
    if (bulkChannelForm.publish_special_deals) {
      payload.publish_special_deals_max = Math.max(1, Number(bulkChannelForm.publish_special_deals_max) || 1);
    }
    if (bulkChannelForm.publish_agent_blocks) {
      payload.publish_agent_blocks_max = Math.max(1, Number(bulkChannelForm.publish_agent_blocks_max) || 1);
    }
    setChannelSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/offline-flights/${flightId}/inventory/channel-publish/bulk`,
        payload,
        REQ
      );
      if (r.data.success) {
        alert(`Updated ${selectedDates.size} date(s)`);
        setBulkChannelOpen(false);
        setSelectedDates(new Set());
        fetchFlightDetails();
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Bulk update failed');
    } finally {
      setChannelSaving(false);
    }
  };

  const publishAllFutureDates = async () => {
    const future = inventory.filter((inv) => inv.date >= todayStr() && inv.status === 'active');
    if (future.length === 0) {
      alert('No future active dates to publish');
      return;
    }
    if (!window.confirm(`Publish all ${future.length} future date(s) to Special Deals and Agent Blocks?`)) return;
    setChannelSaving(true);
    try {
      for (const inv of future) {
        await axios.put(
          `${API}/admin/offline-flights/${flightId}/inventory/${inv.date}/channel-publish`,
          {
            publish_special_deals: true,
            publish_special_deals_max: Math.max(1, inv.available_seats || 1),
            publish_agent_blocks: true,
            publish_agent_blocks_max: Math.max(1, inv.available_seats || 1),
          },
          REQ
        );
      }
      alert(`Published ${future.length} date(s)`);
      fetchFlightDetails();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Publish all failed');
    } finally {
      setChannelSaving(false);
    }
  };

  const channelBadge = (published, remaining, max, soldLabel) => {
    if (!published) return <span className="text-xs text-gray-400">Off</span>;
    return (
      <span className="text-xs">
        <span className="font-semibold text-green-700">On</span>
        <span className="text-gray-600 ml-1">
          {remaining}/{max}
          {soldLabel ? ` · ${soldLabel}` : ''}
        </span>
      </span>
    );
  };

  const downloadManifestPDF = async (date) => {
    try {
      const response = await axios.get(
        `${API}/admin/offline-flights/${flightId}/manifest/${date}/pdf`,
        { ...REQ, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manifest_${flightId}_${date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#659A9B] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  if (!flight) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <p className="text-gray-600">Flight not found</p>
          <button
            onClick={() => navigate('/admin/offline-inventory')}
            className="mt-4 text-[#659A9B] hover:text-[#1B375B]"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totalSeats = inventory.reduce((sum, inv) => sum + inv.total_seats, 0);
  const soldSeats = inventory.reduce((sum, inv) => sum + inv.sold_seats, 0);
  const availableSeats = inventory.reduce((sum, inv) => sum + inv.available_seats, 0);
  const utilizationRate = totalSeats > 0 ? ((soldSeats / totalSeats) * 100).toFixed(1) : 0;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <button
            onClick={() => navigate('/admin/offline-inventory')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1B375B] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-[#1B375B]">Inventory Management</h1>
              <p className="text-gray-600 mt-1">
                {flight.airline_name} {flight.flight_number} • {flight.route.from_airport} → {flight.route.to_airport}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Flight ID</p>
              <p className="text-lg font-bold text-gray-900">{flight.id}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Total Seats</p>
              <p className="text-2xl font-bold text-blue-900">{totalSeats}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">Sold</p>
              <p className="text-2xl font-bold text-green-900">{soldSeats}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-medium">Available</p>
              <p className="text-2xl font-bold text-purple-900">{availableSeats}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm text-orange-600 font-medium">Utilization</p>
              <p className="text-2xl font-bold text-orange-900">{utilizationRate}%</p>
            </div>
          </div>
        </div>

        {/* Channel publishing */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-indigo-100">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-[#1B375B] flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-indigo-600" />
                Channel publishing
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Control which dates appear on Homepage Special Deals and Agent Block Seats, with hard seat caps per channel.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setBulkChannelOpen(true)}
                disabled={selectedDates.size === 0 || channelSaving}
                className="px-3 py-2 text-sm rounded-lg border border-indigo-200 text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
              >
                Bulk edit ({selectedDates.size})
              </button>
              <button
                type="button"
                onClick={publishAllFutureDates}
                disabled={channelSaving}
                className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                Publish all future dates
              </button>
            </div>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-4 text-left text-sm font-semibold text-gray-700 w-10">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Day</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">PNR</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Total Seats</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Sold</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Available</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Special Deals</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Agent Blocks</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Held</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Agent blk</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {inventory.map((inv) => (
                  <tr key={inv.date} className="hover:bg-gray-50">
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        checked={selectedDates.has(inv.date)}
                        onChange={() => toggleDateSelection(inv.date)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-gray-900">{inv.date}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">
                        {inv.day_of_week || new Date(inv.date).toLocaleDateString('en-US', { weekday: 'long' })}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {editingDate === inv.date ? (
                        <input
                          type="text"
                          value={editForm.pnr}
                          onChange={(e) => setEditForm({ ...editForm, pnr: e.target.value.toUpperCase() })}
                          className="px-2 py-1 border border-gray-300 rounded text-sm w-32"
                        />
                      ) : (
                        <p className="font-mono text-sm text-gray-900">{inv.pnr}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingDate === inv.date ? (
                        <input
                          type="number"
                          min="1"
                          value={editForm.total_seats}
                          onChange={(e) => setEditForm({ ...editForm, total_seats: parseInt(e.target.value) })}
                          className="px-2 py-1 border border-gray-300 rounded text-sm w-24"
                        />
                      ) : (
                        <p className="font-semibold text-gray-900">{inv.total_seats}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-green-600">{inv.sold_seats}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className={`font-semibold ${inv.available_seats <= 5 ? 'text-orange-600' : 'text-blue-600'}`}>
                        {inv.available_seats}
                        {inv.available_seats === 0 && <span className="ml-2 text-xs text-red-600">(SOLD OUT)</span>}
                        {inv.available_seats > 0 && inv.available_seats <= 5 && (
                          <span className="ml-2 text-xs text-orange-600">(LOW)</span>
                        )}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      {channelBadge(
                        inv.publish_special_deals,
                        inv.special_deals_remaining ?? 0,
                        inv.publish_special_deals_max ?? 0,
                        `${inv.special_deals_sold ?? 0} sold`
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {channelBadge(
                        inv.publish_agent_blocks,
                        inv.agent_blocks_remaining ?? 0,
                        inv.publish_agent_blocks_max ?? 0,
                        `${inv.agent_blocks_committed ?? 0} committed`
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{inv.held_seats ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{inv.agent_blocked_seats ?? 0}</td>
                    <td className="px-6 py-4">
                      {editingDate === inv.date ? (
                        <select
                          value={editForm.status}
                          onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          <option value="active">Active</option>
                          <option value="blocked">Blocked</option>
                          <option value="sold_out">Sold Out</option>
                        </select>
                      ) : (
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            inv.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : inv.status === 'blocked'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {inv.status}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {editingDate === inv.date ? (
                          <>
                            <button
                              onClick={() => saveInventory(inv.date)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Save"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => openChannelModal(inv)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                              title="Channel publish settings"
                            >
                              <Layers className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => startEditing(inv)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => viewManifest(inv.date)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"
                              title="View Manifest"
                            >
                              <Users className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => downloadManifestCSV(inv.date)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Download CSV"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => downloadManifestPDF(inv.date)}
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                              title="Download PDF"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            {inv.status === 'active' && (
                              <button
                                onClick={() => openAgentBlockModal(inv.date)}
                                className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg"
                                title="Block seats for agent"
                              >
                                <UserPlus className="w-4 h-4" />
                              </button>
                            )}
                            {inv.status === 'blocked' ? (
                              <button
                                onClick={() => unblockInventory(inv.date)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Unblock"
                              >
                                <Unlock className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => blockInventory(inv.date)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Block"
                              >
                                <Lock className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm overflow-hidden mt-6 p-6">
          <h2 className="text-xl font-bold text-[#1B375B] mb-2">Agent block requests (pending)</h2>
          <p className="text-sm text-gray-600 mb-4">
            Agents submitted a request from their portal. Approve to reserve seats and debit wallet; reject to decline
            without charging.
          </p>
          {blockRequests.length === 0 ? (
            <p className="text-gray-500 text-sm">No pending requests for this flight.</p>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg border border-amber-100">
              <table className="w-full text-sm">
                <thead className="bg-amber-100/80 border-b border-amber-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Request</th>
                    <th className="px-3 py-2 text-left">Agent</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Seats</th>
                    <th className="px-3 py-2 text-left">Est. total</th>
                    <th className="px-3 py-2 text-left">Note</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100">
                  {blockRequests.map((rq) => (
                    <tr key={rq.id}>
                      <td className="px-3 py-2 font-mono text-xs">{rq.id}</td>
                      <td className="px-3 py-2 text-xs break-all">{rq.agent_id}</td>
                      <td className="px-3 py-2">{rq.departure_date}</td>
                      <td className="px-3 py-2">{rq.seats}</td>
                      <td className="px-3 py-2">₹{rq.total_amount}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[180px] truncate" title={rq.agent_remarks}>
                        {rq.agent_remarks || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => approveBlockRequest(rq.id)}
                          className="text-sm px-2 py-1 rounded bg-green-600 text-white mr-1 hover:bg-green-700"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => rejectBlockRequest(rq.id)}
                          className="text-sm px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-2xl border border-teal-100 bg-gradient-to-br from-white to-teal-50/30 shadow-sm overflow-hidden">
          <div className="border-b border-teal-100 bg-[#1B375B] px-6 py-4 text-white">
            <h2 className="text-xl font-bold">Agent seat blocks (TBA)</h2>
            <p className="text-sm text-teal-100/90 mt-1">
              Confirmed blocks for this flight. Release returns inventory; wallet and credit-limit blocks get proportional
              refunds (net of penalty). Ledger blocks adjust inventory only.
            </p>
          </div>
          <div className="p-6">
            {agentBlocks.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">No agent seat blocks for this flight yet.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {agentBlocks.map((b) => {
                  const agentLabel = agentLabelById[b.agent_id] || b.agent_id;
                  const st = (b.status || '').toUpperCase();
                  const statusClass =
                    st === 'TICKETED'
                      ? 'bg-emerald-100 text-emerald-800'
                      : st === 'RELEASED' || st === 'CANCELLED'
                        ? 'bg-gray-200 text-gray-700'
                        : 'bg-amber-100 text-amber-900';
                  return (
                    <div
                      key={b.id}
                      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:border-teal-200 transition"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-mono text-sm font-bold text-teal-700">{b.block_code}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{b.id}</p>
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}>{b.status}</span>
                      </div>
                      <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                        <div>
                          <dt className="text-gray-500 text-xs">Travel date</dt>
                          <dd className="font-semibold text-gray-900">{b.departure_date}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 text-xs">Seats</dt>
                          <dd className="font-semibold text-gray-900">{b.seats_blocked}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 text-xs">Amount</dt>
                          <dd className="font-semibold text-[#1B375B]">₹{b.total_amount}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500 text-xs">Debit</dt>
                          <dd className="font-medium text-gray-800 capitalize">{b.debit_mode || '—'}</dd>
                        </div>
                        <div className="col-span-2">
                          <dt className="text-gray-500 text-xs">Agent</dt>
                          <dd className="font-medium text-gray-900 truncate" title={b.agent_id}>
                            {agentLabel}
                          </dd>
                        </div>
                      </dl>
                      {blockCanRelease(b) && (
                        <button
                          type="button"
                          onClick={() => openReleaseModal(b)}
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-900 hover:bg-orange-100"
                        >
                          <Undo2 className="w-4 h-4" />
                          Release seats…
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {releaseModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-bold text-[#1B375B]">Release agent block</h3>
                <button
                  type="button"
                  className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"
                  onClick={() => setReleaseModal(null)}
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                <span className="font-mono font-semibold text-teal-700">{releaseModal.block_code}</span> ·{' '}
                {releaseModal.departure_date} · ₹{releaseModal.total_amount} ({releaseModal.debit_mode})
              </p>
              {(() => {
                const pv = computeReleasePreview(releaseModal, releaseForm);
                return (
                  <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    Preview for <strong>{pv.n}</strong> seat(s): gross <strong>₹{pv.gross}</strong>, penalty{' '}
                    <strong>₹{pv.penalty}</strong>, net <strong>₹{pv.net}</strong>
                  </p>
                );
              })()}
              <p className="mt-3 text-xs text-gray-500">
                Choose how many seats to return to inventory, then optional penalty as % of the proportional refund or
                fixed ₹ per released seat. Wallet mode records a credit for the gross refund and a separate debit for the
                penalty when applicable.
              </p>
              <label className="mt-4 block text-sm font-medium text-gray-700">Seats to release</label>
              <input
                type="number"
                min={1}
                max={releaseModal.seats_blocked}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={releaseForm.seats_to_release}
                onChange={(e) => setReleaseForm((f) => ({ ...f, seats_to_release: e.target.value }))}
              />
              <p className="mt-1 text-xs text-gray-500">
                Max {releaseModal.seats_blocked} (leave as-is for full block). Omit on API when equal to all seats.
              </p>
              <div className="mt-4 space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="radio"
                    className="mt-1"
                    name="penalty_style"
                    checked={releaseForm.penalty_style === 'none'}
                    onChange={() => setReleaseForm((f) => ({ ...f, penalty_style: 'none' }))}
                  />
                  <span>
                    <span className="font-semibold text-gray-900">No penalty</span>
                    <span className="block text-xs text-gray-600">Net refund equals proportional block amount.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="radio"
                    className="mt-1"
                    name="penalty_style"
                    checked={releaseForm.penalty_style === 'percent'}
                    onChange={() => setReleaseForm((f) => ({ ...f, penalty_style: 'percent' }))}
                  />
                  <span>
                    <span className="font-semibold text-gray-900">Penalty: % of refund</span>
                    <span className="block text-xs text-gray-600">Withhold a percentage of the proportional gross refund.</span>
                  </span>
                </label>
                {releaseForm.penalty_style === 'percent' && (
                  <div className="pl-7">
                    <label className="text-xs font-medium text-gray-700">Penalty (% of gross refund)</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      value={releaseForm.penalty_percent}
                      onChange={(e) =>
                        setReleaseForm((f) => ({
                          ...f,
                          penalty_percent: Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 1))
                        }))
                      }
                    />
                  </div>
                )}
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="radio"
                    className="mt-1"
                    name="penalty_style"
                    checked={releaseForm.penalty_style === 'fixed_per_seat'}
                    onChange={() => setReleaseForm((f) => ({ ...f, penalty_style: 'fixed_per_seat' }))}
                  />
                  <span>
                    <span className="font-semibold text-gray-900">Penalty: fixed ₹ per seat released</span>
                    <span className="block text-xs text-gray-600">Total penalty is capped at the gross refund for this release.</span>
                  </span>
                </label>
                {releaseForm.penalty_style === 'fixed_per_seat' && (
                  <div className="pl-7">
                    <label className="text-xs font-medium text-gray-700">₹ per seat released</label>
                    <input
                      type="number"
                      min={0.01}
                      step="0.01"
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      value={releaseForm.penalty_fixed_per_seat}
                      onChange={(e) =>
                        setReleaseForm((f) => ({
                          ...f,
                          penalty_fixed_per_seat: parseFloat(e.target.value) || 0
                        }))
                      }
                    />
                  </div>
                )}
              </div>
              <label className="mt-4 block text-sm font-medium text-gray-700">Internal note (optional)</label>
              <textarea
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                rows={2}
                value={releaseForm.remarks}
                onChange={(e) => setReleaseForm((f) => ({ ...f, remarks: e.target.value }))}
                placeholder="Reason / reference for audit…"
              />
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setReleaseModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={releaseBusy}
                  onClick={submitRelease}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#1B375B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#152a45] disabled:opacity-50"
                >
                  <CreditCard className="h-4 w-4" />
                  {releaseBusy ? 'Processing…' : 'Confirm release'}
                </button>
              </div>
            </div>
          </div>
        )}

        {blockModalDate && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-[#1B375B] mb-2">Block seats for agent</h3>
              <p className="text-sm text-gray-600 mb-4">Date: {blockModalDate}</p>
              <label className="block text-sm font-medium mb-1">Agent</label>
              <select
                className="w-full border rounded px-3 py-2 mb-3"
                value={blockForm.agent_id}
                onChange={(e) => setBlockForm({ ...blockForm, agent_id: e.target.value })}
              >
                <option value="">Select agent</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.agency_name || a.full_name} ({a.id})
                  </option>
                ))}
              </select>
              <label className="block text-sm font-medium mb-1">Seats</label>
              <input
                type="number"
                min={1}
                className="w-full border rounded px-3 py-2 mb-3"
                value={blockForm.seats}
                onChange={(e) => setBlockForm({ ...blockForm, seats: parseInt(e.target.value, 10) || 1 })}
              />
              <label className="block text-sm font-medium mb-1">Fare per seat (₹)</label>
              <input
                type="number"
                className="w-full border rounded px-3 py-2 mb-3"
                value={blockForm.fare_per_seat}
                onChange={(e) => setBlockForm({ ...blockForm, fare_per_seat: e.target.value })}
              />
              <label className="block text-sm font-medium mb-1">Debit mode</label>
              <select
                className="w-full border rounded px-3 py-2 mb-3"
                value={blockForm.debit_mode}
                onChange={(e) => setBlockForm({ ...blockForm, debit_mode: e.target.value })}
              >
                <option value="wallet">Wallet</option>
                <option value="credit_limit">Credit limit</option>
                <option value="ledger">Ledger (no wallet movement)</option>
              </select>
              <label className="block text-sm font-medium mb-1">Remarks</label>
              <textarea
                className="w-full border rounded px-3 py-2 mb-4"
                rows={2}
                value={blockForm.remarks}
                onChange={(e) => setBlockForm({ ...blockForm, remarks: e.target.value })}
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  className="px-4 py-2 border rounded"
                  onClick={() => setBlockModalDate(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[#659A9B] text-white rounded disabled:opacity-50"
                  disabled={blockSaving}
                  onClick={submitAgentBlock}
                >
                  {blockSaving ? 'Saving…' : 'Confirm block'}
                </button>
              </div>
            </div>
          </div>
        )}

        {channelModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-bold text-[#1B375B]">Channel publish — {channelModal.date}</h3>
                <button type="button" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100" onClick={() => setChannelModal(null)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Available inventory: <strong>{channelModal.available_seats}</strong> seats
              </p>
              <div className="mt-4 space-y-4">
                <div className="rounded-lg border border-indigo-100 p-4 bg-indigo-50/40">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <input
                      type="checkbox"
                      checked={channelForm.publish_special_deals}
                      onChange={(e) =>
                        setChannelForm({
                          ...channelForm,
                          publish_special_deals: e.target.checked,
                          publish_special_deals_max: e.target.checked
                            ? channelForm.publish_special_deals_max || channelModal.available_seats || 1
                            : channelForm.publish_special_deals_max,
                        })
                      }
                    />
                    Homepage Special Deals
                  </label>
                  {channelForm.publish_special_deals && (
                    <div className="mt-2">
                      <label className="text-xs text-gray-600">Max seats (hard cap)</label>
                      <input
                        type="number"
                        min={Math.max(1, channelModal.special_deals_sold || 0)}
                        max={channelModal.total_seats}
                        className="mt-1 w-full border rounded px-3 py-2 text-sm"
                        value={channelForm.publish_special_deals_max}
                        onChange={(e) =>
                          setChannelForm({ ...channelForm, publish_special_deals_max: parseInt(e.target.value, 10) || 0 })
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Remaining: {channelModal.special_deals_remaining ?? 0} · Sold via channel:{' '}
                        {channelModal.special_deals_sold ?? 0}
                      </p>
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-teal-100 p-4 bg-teal-50/40">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <input
                      type="checkbox"
                      checked={channelForm.publish_agent_blocks}
                      onChange={(e) =>
                        setChannelForm({
                          ...channelForm,
                          publish_agent_blocks: e.target.checked,
                          publish_agent_blocks_max: e.target.checked
                            ? channelForm.publish_agent_blocks_max || channelModal.available_seats || 1
                            : channelForm.publish_agent_blocks_max,
                        })
                      }
                    />
                    Agent Block Seats catalog
                  </label>
                  {channelForm.publish_agent_blocks && (
                    <div className="mt-2">
                      <label className="text-xs text-gray-600">Max seats (hard cap)</label>
                      <input
                        type="number"
                        min={Math.max(1, channelModal.agent_blocks_committed || 0)}
                        max={channelModal.total_seats}
                        className="mt-1 w-full border rounded px-3 py-2 text-sm"
                        value={channelForm.publish_agent_blocks_max}
                        onChange={(e) =>
                          setChannelForm({ ...channelForm, publish_agent_blocks_max: parseInt(e.target.value, 10) || 0 })
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Remaining: {channelModal.agent_blocks_remaining ?? 0} · Committed:{' '}
                        {channelModal.agent_blocks_committed ?? 0}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <button type="button" className="px-4 py-2 border rounded" onClick={() => setChannelModal(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
                  disabled={channelSaving}
                  onClick={submitChannelModal}
                >
                  {channelSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {bulkChannelOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-bold text-[#1B375B]">Bulk channel publish</h3>
                <button type="button" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100" onClick={() => setBulkChannelOpen(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600">{selectedDates.size} date(s) selected</p>
              <div className="mt-4 space-y-4">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={bulkChannelForm.publish_special_deals}
                    onChange={(e) => setBulkChannelForm({ ...bulkChannelForm, publish_special_deals: e.target.checked })}
                  />
                  Enable Special Deals
                </label>
                {bulkChannelForm.publish_special_deals && (
                  <input
                    type="number"
                    min={1}
                    placeholder="Max seats per date"
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={bulkChannelForm.publish_special_deals_max}
                    onChange={(e) => setBulkChannelForm({ ...bulkChannelForm, publish_special_deals_max: e.target.value })}
                  />
                )}
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={bulkChannelForm.publish_agent_blocks}
                    onChange={(e) => setBulkChannelForm({ ...bulkChannelForm, publish_agent_blocks: e.target.checked })}
                  />
                  Enable Agent Blocks
                </label>
                {bulkChannelForm.publish_agent_blocks && (
                  <input
                    type="number"
                    min={1}
                    placeholder="Max seats per date"
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={bulkChannelForm.publish_agent_blocks_max}
                    onChange={(e) => setBulkChannelForm({ ...bulkChannelForm, publish_agent_blocks_max: e.target.value })}
                  />
                )}
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <button type="button" className="px-4 py-2 border rounded" onClick={() => setBulkChannelOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
                  disabled={channelSaving}
                  onClick={submitBulkChannel}
                >
                  {channelSaving ? 'Saving…' : 'Apply to selected'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryManagement;
